import { useState } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { SwipeCard } from "./SwipeCard"
import { useIsMobile } from "../hooks/useIsMobile"

interface Props {
  items: string[]
  language: "tr" | "en"
  onNext: (decisions: string[], noise: string[]) => void
}

export const Step2Filter = ({ items, language, onNext }: Props) => {
  const isMobile = useIsMobile()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [decisions, setDecisions] = useState<string[]>([])
  const [noise, setNoise] = useState<string[]>([])
  const [phase, setPhase] = useState<"swipe" | "reducing" | "done">("swipe")
  const [lastSwipe, setLastSwipe] = useState<"left" | "right" | null>(null)

  const t = language === "tr" ? {
    stepLabel: "Adım 2",
    title: "Hangisi daha önemli?",
    counter: (n: number, total: number) => `${n} / ${total}`,
    importantCount: (n: number) => `${n} önemli`,
    continueBtn: "Devam Et",
    noDecisions: "Hepsini 'bekleyebilir' işaretledin. En az 1 tane önemli seç.",
    reducingLabel: (n: number) => n === 1 ? "Daha az. Daha net." : `${n} önemli`,
  } : {
    stepLabel: "Step 2",
    title: "What matters most?",
    counter: (n: number, total: number) => `${n} / ${total}`,
    importantCount: (n: number) => `${n} important`,
    continueBtn: "Continue",
    noDecisions: "You marked everything as 'can wait'. Select at least 1 important item.",
    reducingLabel: (n: number) => n === 1 ? "Less. Clearer." : `${n} matter`,
  }

  const handleSwipe = (direction: "left" | "right") => {
    if (currentIndex >= items.length) return
    setLastSwipe(direction)
    const currentItem = items[currentIndex]
    if (direction === "right") setDecisions(prev => [...prev, currentItem])
    else setNoise(prev => [...prev, currentItem])
    setCurrentIndex(prev => prev + 1)
  }

  const allFiltered = currentIndex >= items.length

  const handleContinue = () => {
    setPhase("reducing")
    setTimeout(() => {
      setPhase("done")
      setTimeout(() => onNext(decisions, noise), 500)
    }, 800)
  }

  if (phase === "reducing" || phase === "done") {
    return (
      <div className="reducing-wrapper">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="reducing-label"
        >
          {t.reducingLabel(decisions.length)}
        </motion.div>
        <div className="reducing-list">
          <AnimatePresence>
            {decisions.map((d, i) => (
              <motion.div
                key={d}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                className="list-item"
              >
                <div className="list-item-bullet" />
                <span className="list-item-text">{d}</span>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="step-label">{t.stepLabel}</div>
      <div className="step-title">{t.title}</div>

      <div className="swipe-area">
        <AnimatePresence mode="wait">
          {currentIndex < items.length ? (
            <motion.div
              key={currentIndex}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, x: lastSwipe === "right" ? 40 : -40, scale: 0.95 }}
              transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            >
              <SwipeCard
                id={String(currentIndex)}
                text={items[currentIndex]}
                onSwipe={handleSwipe}
                isMobile={isMobile}
                language={language}
                isFirst={currentIndex === 0}
              />
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="swipe-done-check"
            >
              ✓
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <div className="swipe-counter">
        <span>{t.counter(Math.min(currentIndex, items.length), items.length)}</span>
        {decisions.length > 0 && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="swipe-important-count"
          >
            {t.importantCount(decisions.length)}
          </motion.span>
        )}
      </div>

      {allFiltered && decisions.length > 0 && (
        <motion.button
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
          onClick={handleContinue}
          className="btn-primary"
        >
          {t.continueBtn}
        </motion.button>
      )}

      {allFiltered && decisions.length === 0 && (
        <p className="swipe-no-decisions">{t.noDecisions}</p>
      )}
    </div>
  )
}